import { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, User, Store, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const vendorSchema = z.object({
  email: z.string().email('Email invalide').optional(),
  password: z.string().min(6, 'Minimum 6 caractères').optional(),
  full_name: z.string().min(2, 'Minimum 2 caractères').optional(),
  business_name: z.string().min(2, 'Minimum 2 caractères'),
  phone: z.string().optional(),
  is_internal: z.boolean(),
  employee_id: z.string().optional(), // Pour vendeurs internes
  manager_ids: z.array(z.string()).max(5, 'Maximum 5 gestionnaires'),
}).refine((data) => {
  if (data.is_internal) {
    return !!data.employee_id; // Si interne, employee_id requis
  } else {
    return !!data.email && !!data.password && !!data.full_name; // Si externe, compte utilisateur requis
  }
}, {
  message: "Les champs requis varient selon le type de vendeur",
});

type VendorFormData = z.infer<typeof vendorSchema>;

interface User {
  id: string;
  full_name: string;
  phone: string | null;
  role: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  position: string;
}

interface AddVendorFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddVendorForm = ({ open, onOpenChange, onSuccess }: AddVendorFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const form = useForm<VendorFormData>({
    resolver: zodResolver(vendorSchema),
    defaultValues: {
      email: '',
      password: '',
      full_name: '',
      business_name: '',
      phone: '',
      is_internal: false,
      employee_id: '',
      manager_ids: [],
    },
  });

  // Charger les utilisateurs disponibles
  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, phone, system_role')
        .neq('role', 'vendor') // Exclure les vendeurs existants
        .order('full_name');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  // Charger les employés disponibles
  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name, phone, email, position')
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des employés:', error);
    }
  };

  // Charger les utilisateurs et employés quand le dialog s'ouvre
  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchEmployees();
    }
  }, [open]);

  const selectedManagers = form.watch('manager_ids');
  const availableUsers = users.filter(user => 
    !selectedManagers.includes(user.id) &&
    (user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
     user.phone?.includes(searchQuery))
  );

  const getSelectedUsers = () => {
    return users.filter(user => selectedManagers.includes(user.id));
  };

  const addManager = (userId: string) => {
    const currentManagers = form.getValues('manager_ids');
    if (currentManagers.length < 5 && !currentManagers.includes(userId)) {
      form.setValue('manager_ids', [...currentManagers, userId]);
    }
  };

  const removeManager = (userId: string) => {
    const currentManagers = form.getValues('manager_ids');
    form.setValue('manager_ids', currentManagers.filter(id => id !== userId));
  };

  const onSubmit = async (data: VendorFormData) => {
    setLoading(true);
    try {
      let vendorId: string;
      
      if (data.is_internal) {
        // Vendeur interne : créer un profil lié à un employé existant
        vendorId = crypto.randomUUID();
        
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: vendorId,
            role: 'vendor',
            vendor_status: 'approved',
            business_name: data.business_name,
            is_internal: true,
            employee_id: data.employee_id,
            phone: data.phone,
          });

        if (profileError) throw profileError;
      } else {
        // Vendeur externe : créer un compte utilisateur complet
        const { data: vendorData, error: vendorError } = await supabase.auth.signUp({
          email: data.email!,
          password: data.password!,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              full_name: data.full_name!,
              business_name: data.business_name,
              phone: data.phone,
            },
          },
        });

        if (vendorError) throw vendorError;
        if (!vendorData.user?.id) throw new Error('Erreur création utilisateur');
        
        vendorId = vendorData.user.id;

        // Mettre à jour le profil vendeur
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            role: 'vendor',
            vendor_status: 'approved',
            business_name: data.business_name,
            is_internal: false,
          })
          .eq('id', vendorId);

        if (profileError) throw profileError;
      }

      // Lier les gestionnaires sélectionnés au vendeur
      if (data.manager_ids.length > 0) {
        const managerLinks = data.manager_ids.map(managerId => ({
          vendor_id: vendorId,
          manager_id: managerId,
        }));

        const { error: linkError } = await supabase
          .from('vendor_managers')
          .insert(managerLinks);

        if (linkError) {
          console.error('Erreur liaison gestionnaires:', linkError);
        }
      }

      toast({
        title: 'Vendeur créé avec succès',
        description: `${data.business_name} (${data.is_internal ? 'interne' : 'externe'}) avec ${data.manager_ids.length} gestionnaire(s) assigné(s).`,
      });

      form.reset();
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Erreur lors de la création',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            Ajouter un vendeur
          </DialogTitle>
          <DialogDescription>
            Créez un nouveau compte vendeur avec ses gestionnaires (jusqu'à 5)
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Informations vendeur */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informations du vendeur</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <Switch
                  id="is-internal"
                  checked={form.watch('is_internal')}
                  onCheckedChange={(checked) => {
                    form.setValue('is_internal', checked);
                    // Reset des champs selon le type
                    if (checked) {
                      form.setValue('email', '');
                      form.setValue('password', '');
                      form.setValue('full_name', '');
                    } else {
                      form.setValue('employee_id', '');
                    }
                  }}
                />
                <Label htmlFor="is-internal">Vendeur interne (lié à un employé)</Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {form.watch('is_internal') ? (
                  // Champs pour vendeur interne
                  <>
                    <div className="col-span-2">
                      <Label htmlFor="employee-select">Employé à promouvoir vendeur *</Label>
                      <Select 
                        value={form.watch('employee_id')} 
                        onValueChange={(value) => form.setValue('employee_id', value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner un employé..." />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map((employee) => (
                            <SelectItem key={employee.id} value={employee.id}>
                              {employee.first_name} {employee.last_name} - {employee.position}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.employee_id && (
                        <p className="text-sm text-destructive mt-1">
                          Veuillez sélectionner un employé
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  // Champs pour vendeur externe
                  <>
                    <div>
                      <Label htmlFor="vendor-email">Email du vendeur *</Label>
                      <Input
                        id="vendor-email"
                        type="email"
                        {...form.register('email')}
                        className="mt-1"
                      />
                      {form.formState.errors.email && (
                        <p className="text-sm text-destructive mt-1">
                          {form.formState.errors.email.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="vendor-password">Mot de passe *</Label>
                      <Input
                        id="vendor-password"
                        type="password"
                        {...form.register('password')}
                        className="mt-1"
                      />
                      {form.formState.errors.password && (
                        <p className="text-sm text-destructive mt-1">
                          {form.formState.errors.password.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="vendor-name">Nom complet *</Label>
                      <Input
                        id="vendor-name"
                        {...form.register('full_name')}
                        className="mt-1"
                      />
                      {form.formState.errors.full_name && (
                        <p className="text-sm text-destructive mt-1">
                          {form.formState.errors.full_name.message}
                        </p>
                      )}
                    </div>
                  </>
                )}
                
                <div>
                  <Label htmlFor="business-name">Nom commercial *</Label>
                  <Input
                    id="business-name"
                    {...form.register('business_name')}
                    className="mt-1"
                    placeholder="Le nom qui apparaîtra sur la plateforme"
                  />
                  {form.formState.errors.business_name && (
                    <p className="text-sm text-destructive mt-1">
                      {form.formState.errors.business_name.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="vendor-phone">Téléphone</Label>
                  <Input
                    id="vendor-phone"
                    {...form.register('phone')}
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Gestionnaires */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Gestionnaires
                    <Badge variant="secondary">{selectedManagers.length}/5</Badge>
                  </CardTitle>
                  <CardDescription>
                    Sélectionnez jusqu'à 5 utilisateurs existants comme gestionnaires
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Barre de recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un utilisateur..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Gestionnaires sélectionnés */}
              {getSelectedUsers().length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Gestionnaires sélectionnés</Label>
                  <div className="mt-2 space-y-2">
                    {getSelectedUsers().map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border">
                        <div>
                          <p className="font-medium">{user.full_name}</p>
                          <p className="text-sm text-muted-foreground">{user.phone}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeManager(user.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Liste des utilisateurs disponibles */}
              {availableUsers.length > 0 && selectedManagers.length < 5 && (
                <div>
                  <Label className="text-sm font-medium">Utilisateurs disponibles</Label>
                  <div className="mt-2 max-h-48 overflow-y-auto space-y-2">
                    {availableUsers.slice(0, 10).map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                        <div>
                          <p className="font-medium">{user.full_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {user.phone} • {user.role}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addManager(user.id)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Ajouter
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {availableUsers.length === 0 && selectedManagers.length < 5 && (
                <p className="text-muted-foreground text-center py-4">
                  Aucun utilisateur disponible trouvé.
                </p>
              )}
            </CardContent>
          </Card>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Création...' : 'Créer le vendeur'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};